'use client'

import {
  hotspotButtonStyle,
  hotspotFontSize,
  hotspotMotionStyle,
  type HotspotAppearance,
  type HotspotButtonIcon,
} from '@/lib/hotspotButton'
import { ArrowRight, ExternalLink, Play } from 'lucide-react'
import { FC, useEffect, useState } from 'react'

export type HotspotButtonVisualProps = {
  label: string
  appearance: HotspotAppearance
  /** 表示中のボタンの大きさ（px）。余白・角丸を高さに比例させる */
  width: number
  height: number
  /** 実際の映像の表示幅。文字サイズを映像と一緒に拡縮する */
  videoWidth: number
  brandColor: string | null | undefined
  /** 登場アニメーションを再生するか（エディタの編集中は止めた絵で見せる） */
  animate?: boolean
  /** 退場アニメーションの途中 */
  exiting?: boolean
  isHover?: boolean
}

const ICONS: Record<Exclude<HotspotButtonIcon, 'NONE'>, typeof Play> = {
  PLAY: Play,
  ARROW: ArrowRight,
  EXTERNAL: ExternalLink,
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true

/**
 * 動画内の見えるボタンの絵（ADR-0024）。当たり判定は親（`<button>`）が持ち、ここは描くだけ。
 * プレイヤー（`WideHotspotLayer`）とエディタ（`HotspotStage`）で共有する。
 */
const HotspotButtonVisual: FC<HotspotButtonVisualProps> = ({
  label,
  appearance,
  width,
  height,
  videoWidth,
  brandColor,
  animate = false,
  exiting = false,
  isHover = false,
}) => {
  const shouldAnimate = animate && appearance.enter !== 'NONE'
  const [entered, setEntered] = useState(!shouldAnimate)

  // 初回は「登場前」の姿で描き、次のフレームで「登場後」に切り替えて transition を走らせる
  useEffect(() => {
    if (!shouldAnimate || prefersReducedMotion()) {
      setEntered(true)
      return
    }
    let second = 0
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setEntered(true))
    })
    return () => {
      cancelAnimationFrame(first)
      cancelAnimationFrame(second)
    }
  }, [shouldAnimate])

  const Icon = appearance.icon === 'NONE' ? null : ICONS[appearance.icon]
  const iconSize = hotspotFontSize(videoWidth) * 1.05

  return (
    <span
      aria-hidden="true"
      style={{
        display: 'block',
        width: '100%',
        height: '100%',
        ...hotspotMotionStyle(appearance, entered, exiting),
      }}
    >
      <span style={hotspotButtonStyle(appearance, { width, height }, videoWidth, brandColor, isHover)}>
        {Icon && <Icon size={iconSize} strokeWidth={2.5} style={{ flexShrink: 0 }} />}
        <span
          style={{
            minWidth: 0,
            maxWidth: '14.28em',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            wordBreak: 'normal',
            textAlign: 'center',
          }}
        >
          {label}
        </span>
      </span>
    </span>
  )
}

export default HotspotButtonVisual
